// Client POV — facility add page used by logged-in client users
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'react-feather';
import Loader from '@/app/routes/Loader';
import CreateLayout from '@/shared/layout/createLayout';
import { TextField } from '@/shared/components/TextField';
import { Snackbar } from '@/shared/components/Snackbar';
import { useAddFacilityPage } from '../../hooks/useAddFacilityPage';
import { useAddEquipment } from '../../hooks/useAddEquipment';
import { useClientId } from '@/shared/hooks/useClientId';
import { useClientStatus } from '@/shared/hooks/useClientStatus';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import { Tooltip } from '@/shared/components/Tooltip/Tooltip';
import { FACILITY_LABELS } from '../../constants/facilityLabels';
import { EQUIPMENT_ERROR_MESSAGES } from '../../errors/errorMessages';
import { EQUIPMENT_ERROR_MESSAGES as TL } from '../../errors/translations/errorMessages';
import { getFacilitySession } from '../../utils/facilitySessionStorage';
import { AddEquipmentForm } from '../../components/AddEquipmentForm';
import { AddEmployeeForm } from '../../components/AddEmployeeForm';
import { EquipmentList } from '../../components/EquipmentList/EquipmentList';
import { EmployeeList } from '../../components/EmployeeList/EmployeeList';
import type { FacilityEntryErrors } from '../../types/addFacilityType';

const FIELD_ORDER: (keyof FacilityEntryErrors)[] = [
  'name',
  'phone',
  'address1',
  'city',
  'state',
  'zip',
  'nip',
];

type TabId = 'aparatura' | 'pracownicy';

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AddFacilityPage() {
  const navigate = useNavigate();
  const { clientId } = useClientId();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();
  const { clientStatus } = useClientStatus();
  const canCreateFacilityUser =
    privilegesLoading || !clientId ? true : privileges.canCreateFacilityUser;
  const canEditFacilityUser =
    privilegesLoading || !clientId ? false : privileges.canEditFacilityUser;
  const canToggleEmployeeStatus =
    privilegesLoading || !clientId ? false : privileges.canToggleEmployeeStatus;
  const canCreateEquipment =
    privilegesLoading || !clientId ? true : privileges.canCreateEquipment;
  const canEditEquipment =
    privilegesLoading || !clientId ? false : privileges.canEditEquipment;

  const isToggleDisabled = clientStatus != null && clientStatus !== 'active';
  const toggleDisabledReason = isToggleDisabled
    ? FACILITY_LABELS.TOGGLE_EMPLOYEE_DISABLED_CLIENT
    : undefined;
  // On the Add Facility page, the equipment and employee tabs are gated entirely
  // on the create privilege — not view/list. This is intentional: if a client no
  // longer has the create privilege, showing the existing list would be misleading
  // because any equipment or employee added here would be linked to this new
  // facility. Without the create privilege that action is impossible, so surfacing
  // the list implies a capability the user does not have. The tab is disabled,
  // its count shows 0, the list is hidden, and the "+" button is not rendered.
  const submitRequestedRef = useRef(false);
  const [activeTab, setActiveTab] = useState<TabId>('aparatura');

  const {
    entry,
    errors,
    isSubmitting,
    isDirty,
    isCancelModalOpen,
    isSnackbarOpen,
    snackbarError,
    isSuccess,
    serverFacilityId,
    getOrCreateTempFacility,
    updateField,
    handleNipChange,
    handlePhoneChange,
    submit,
    clearSession,
    handleCancel,
    handleCloseCancelModal,
    handleCloseSnackbar,
    conflictError,
    serialNumberConflict,
    clearSerialNumberConflict,
    isRehydrating,
    initialEmployeeCount,
    // Employee
    activeEmployeeEntry,
    employeeErrorsMap,
    isEmployeeFormDirty,
    isEmployeeSubmitting,
    isEmployeeSuccessOpen,
    submittedEmployeeCount,
    setIsEmployeeSuccessOpen,
    updateEmployeeField,
    updateEmployeeGender,
    submitEmployee,
  } = useAddFacilityPage(clientId);

  // Always keep a ref to the latest clearSession so the unmount cleanup
  // captures the version that has the resolved clientId, not the initial null.
  const clearSessionRef = useRef(clearSession);
  clearSessionRef.current = clearSession;

  // Clear temp facility session on unmount (covers route-change / browser-back
  // navigation that bypasses the cancel or submit flow). Harmless if already cleared.
  useEffect(() => {
    return () => {
      clearSessionRef.current();
    };
  }, []);

  const equipment = useAddEquipment();
  const [equipmentServerIds, setEquipmentServerIds] = useState<
    Record<string, true>
  >({});
  const [isEquipmentSuccessOpen, setIsEquipmentSuccessOpen] = useState(false);
  const [isEquipmentErrorOpen, setIsEquipmentErrorOpen] = useState(false);
  const [equipmentErrorMessage, setEquipmentErrorMessage] = useState('');
  const [isAddingEquipment, setIsAddingEquipment] = useState(true);
  const [isEquipmentSubmitting, setIsEquipmentSubmitting] = useState(false);
  const [isEquipmentLoading, setIsEquipmentLoading] = useState(false);
  const isEquipmentLoadingRef = useRef(false);
  const [isAddingEmployee, setIsAddingEmployee] = useState(true);
  const [facilityUserTotalCount, setFacilityUserTotalCount] = useState(0);
  const [equipmentTotalCount, setEquipmentTotalCount] = useState(0);
  const [isEquipmentAccessDenied, setIsEquipmentAccessDenied] = useState(false);
  const [isEmployeeAccessDenied, setIsEmployeeAccessDenied] = useState(false);
  const [equipmentListResetKey, setEquipmentListResetKey] = useState(0);
  const [employeeListResetKey, setEmployeeListResetKey] = useState(0);
  const [equipmentEditCount, setEquipmentEditCount] = useState(0);
  const [isEquipmentEditDirty, setIsEquipmentEditDirty] = useState(false);
  const [isEmployeeEditDirty, setIsEmployeeEditDirty] = useState(false);
  const [serialToEquipmentId, setSerialToEquipmentId] = useState<
    Record<string, string>
  >({});
  const prevSubmittedEmployeeCountRef = useRef(submittedEmployeeCount);

  // Once rehydration completes, sync employee state from fetched count
  useEffect(() => {
    if (!isRehydrating) {
      setFacilityUserTotalCount(initialEmployeeCount);
      if (initialEmployeeCount > 0) {
        setIsAddingEmployee(false);
      }
    }
  }, [isRehydrating, initialEmployeeCount]);

  useEffect(() => {
    if (submittedEmployeeCount > prevSubmittedEmployeeCountRef.current) {
      setIsAddingEmployee(false);
    }
    prevSubmittedEmployeeCountRef.current = submittedEmployeeCount;
  }, [submittedEmployeeCount]);

  // Detect session restore on mount — show tab-level loader while equipment list fetches
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!clientId || hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;
    const stored = getFacilitySession(clientId);
    if (stored[0]) {
      isEquipmentLoadingRef.current = true;
      setIsEquipmentLoading(true);
    }
  }, [clientId]);

  const handleEmployeeAccessDenied = useCallback(() => {
    setIsEmployeeAccessDenied(true);
  }, []);

  const handleFacilityUserTotalCountChange = useCallback((count: number) => {
    setFacilityUserTotalCount(count);
  }, []);

  const handleEquipmentAccessDenied = useCallback(() => {
    isEquipmentLoadingRef.current = false;
    setIsEquipmentLoading(false);
    setIsEquipmentAccessDenied(true);
  }, []);

  // Stable callback: updates count + clears loader on first arrival + clears serial conflict
  const handleEquipmentTotalCountChange = useCallback(
    (count: number) => {
      setEquipmentTotalCount(count);
      clearSerialNumberConflict();
      if (isEquipmentLoadingRef.current) {
        isEquipmentLoadingRef.current = false;
        setIsEquipmentLoading(false);
        if (count > 0) {
          setIsAddingEquipment(false);
        }
      }
    },
    [clearSerialNumberConflict]
  );

  // Auto-switch tabs when access is revoked mid-session
  useEffect(() => {
    if (
      isEquipmentAccessDenied &&
      activeTab === 'aparatura' &&
      canCreateFacilityUser &&
      !isEmployeeAccessDenied
    ) {
      setActiveTab('pracownicy');
    }
  }, [
    isEquipmentAccessDenied,
    activeTab,
    canCreateFacilityUser,
    isEmployeeAccessDenied,
  ]);

  useEffect(() => {
    if (
      isEmployeeAccessDenied &&
      activeTab === 'pracownicy' &&
      canCreateEquipment &&
      !isEquipmentAccessDenied
    ) {
      setActiveTab('aparatura');
    }
  }, [
    isEmployeeAccessDenied,
    activeTab,
    canCreateEquipment,
    isEquipmentAccessDenied,
  ]);

  const isEntryDirty = (e: (typeof equipment.equipmentList)[number]) =>
    e.name !== '' ||
    e.description !== '' ||
    e.category !== '' ||
    e.serialNumber !== '' ||
    e.modelName !== '' ||
    e.dateOfProduction !== undefined ||
    e.imageFile !== undefined ||
    e.docFiles.length > 0;

  const pendingEntries = equipment.equipmentList.filter(
    (e) => !equipmentServerIds[e.id]
  );
  const hasDirtyPendingForm = pendingEntries.some(isEntryDirty);
  const submittedCount = Object.keys(equipmentServerIds).length;

  // Scroll to first error field after facility submit attempt
  useEffect(() => {
    if (!submitRequestedRef.current) {
      return;
    }
    submitRequestedRef.current = false;

    const firstErrorField = FIELD_ORDER.find((f) => Boolean(errors[f]));
    if (!firstErrorField) {
      return;
    }

    document
      .querySelector(`[data-field="facility-${firstErrorField}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [errors]);

  // Navigate to listing once facility is created and no dirty pending equipment
  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    if (hasDirtyPendingForm) {
      return;
    }
    navigate('/Placówki', {
      state: { successMessage: FACILITY_LABELS.ADD_FACILITY_SUCCESS },
    });
  }, [isSuccess, hasDirtyPendingForm, navigate]);

  // Switch to Pracownicy tab when a 409 conflict identifies a conflicting employee
  useEffect(() => {
    if (conflictError) {
      setActiveTab('pracownicy');
      setIsAddingEmployee(false);
    }
  }, [conflictError]);

  // Auto-switch to the valid tab when one privilege is revoked on load/refresh.
  // If both are revoked, stay on the current tab (no-permission message shown in content).
  useEffect(() => {
    if (privilegesLoading) {
      return;
    }
    if (
      activeTab === 'aparatura' &&
      !canCreateEquipment &&
      canCreateFacilityUser
    ) {
      setActiveTab('pracownicy');
    } else if (
      activeTab === 'pracownicy' &&
      !canCreateFacilityUser &&
      canCreateEquipment
    ) {
      setActiveTab('aparatura');
    }
  }, [privilegesLoading, activeTab, canCreateEquipment, canCreateFacilityUser]);

  const handleEquipmentSubmit = async (id: string) => {
    const isValid = equipment.validateEntry(id);
    if (!isValid || !clientId) {
      return;
    }
    // Start loader immediately — covers the getOrCreateTempFacility call
    // (which may hit the API) before equipment.isSubmitting kicks in.
    setIsEquipmentSubmitting(true);
    try {
      const facilityId = await getOrCreateTempFacility(clientId);
      if (!facilityId) {
        return;
      }
      const result = await equipment.submitEntry(id, facilityId, clientId);
      if (result.success) {
        setEquipmentServerIds((prev) => ({ ...prev, [id]: true }));
        const serial = equipment.equipmentList
          .find((e) => e.id === id)
          ?.serialNumber?.trim();
        const eqId = result.equipmentId;
        if (serial && eqId) {
          setSerialToEquipmentId((prev) => ({ ...prev, [serial]: eqId }));
        }
        setIsAddingEquipment(false);
        setIsEquipmentSuccessOpen(true);
      } else if (result.error) {
        if (result.error === EQUIPMENT_ERROR_MESSAGES.ACCESS_DENIED) {
          setIsEquipmentAccessDenied(true);
        }
        setEquipmentErrorMessage(result.error);
        setIsEquipmentErrorOpen(true);
      }
    } finally {
      setIsEquipmentSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId) {
      return;
    }
    submitRequestedRef.current = true;
    void submit(clientId);
  };

  const handleCancelClick = () => {
    const isAnyDirty = isDirty || hasDirtyPendingForm || submittedCount > 0;
    if (!isAnyDirty) {
      clearSession();
      navigate('/Placówki');
      return;
    }
    handleCancel();
  };

  const handleTabChange = (tab: TabId) => {
    if (activeTab === 'pracownicy' && tab !== 'pracownicy') {
      // Always clear fields — also clears lingering errors even when the user
      // manually emptied the field (making isEmployeeFormDirty false) before switching.
      updateEmployeeField(activeEmployeeEntry.id, 'name', '');
      updateEmployeeField(activeEmployeeEntry.id, 'email', '');
      updateEmployeeField(activeEmployeeEntry.id, 'phone', '');
      updateEmployeeField(activeEmployeeEntry.id, 'academicTitle', '');
      updateEmployeeGender(activeEmployeeEntry.id, undefined);
      // Collapse to list if employees exist either in-session or already in DB.
      if (submittedEmployeeCount > 0 || facilityUserTotalCount > 0) {
        setIsAddingEmployee(false);
      }
      setEmployeeListResetKey((prev) => prev + 1);
    }
    if (activeTab === 'aparatura' && tab !== 'aparatura') {
      clearSerialNumberConflict();
      if (hasDirtyPendingForm) {
        pendingEntries.forEach((e) => {
          if (isEntryDirty(e)) {
            if (submittedCount > 0 || equipmentTotalCount > 0) {
              equipment.removeEntry(e.id);
            } else {
              equipment.updateField(e.id, 'name', '');
              equipment.updateField(e.id, 'description', '');
              equipment.updateField(e.id, 'serialNumber', '');
              equipment.updateField(e.id, 'modelName', '');
              equipment.updateCategory(e.id, '');
              equipment.updateDateOfProduction(e.id, undefined);
              equipment.handleImageDelete(e.id);
              equipment.handleDocsChange(e.id, []);
            }
          }
        });
      }
      // Collapse to list if equipment exists either in-session or already in DB.
      if (submittedCount > 0 || equipmentTotalCount > 0) {
        setIsAddingEquipment(false);
      }
      setEquipmentListResetKey((prev) => prev + 1);
      setEquipmentListResetKey((prev) => prev + 1);
    }
    setActiveTab(tab);
  };

  const submitButtonLabel = FACILITY_LABELS.SUBMIT_BUTTON;
  const submitButtonLoadingLabel = FACILITY_LABELS.SUBMIT_BUTTON_LOADING;

  const submitButtonDisabled =
    isSubmitting ||
    isEmployeeSubmitting ||
    isRehydrating ||
    !clientId ||
    isSuccess ||
    isEmployeeFormDirty ||
    hasDirtyPendingForm ||
    isEquipmentEditDirty ||
    isEmployeeEditDirty;
  return (
    <>
      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancelClick}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={() => {
          clearSession();
          handleCloseCancelModal();
          navigate('/Placówki');
        }}
        isSnackbarOpen={false}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarError=""
        submitButtonLabel={submitButtonLabel}
        submitButtonLoadingLabel={submitButtonLoadingLabel}
        cancelButtonLabel={FACILITY_LABELS.CANCEL_BUTTON}
        cancelButtonDisabled={isSubmitting || isEmployeeSubmitting}
        submitButtonDisabled={submitButtonDisabled}
        headerClassName="py-[10px] mb-[20px]"
        Header={() => (
          <h1 className="text-[22px] font-normal leading-7 text-[#181A1B]">
            {FACILITY_LABELS.FORM_TITLE}
          </h1>
        )}
      >
        {isRehydrating ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader />
          </div>
        ) : null}
        <div
          className={
            isRehydrating ? 'hidden' : 'rounded-[12px] flex flex-col gap-8'
          }
        >
          {/* ── Basic details ─────────────────────────────────────────────── */}
          <div className="flex flex-col gap-2">
            <p className="text-[18px] font-normal text-on-surface-variant">
              {FACILITY_LABELS.SECTION_BASIC_DETAILS}
            </p>

            <div className="flex gap-4">
              <div className="flex-1" data-field="facility-name">
                <TextField
                  fullWidth
                  label={FACILITY_LABELS.FIELD_NAME}
                  autoTrimMaxLength={200}
                  value={entry.name}
                  onChange={(e) =>
                    updateField('name', e.target.value.trimStart())
                  }
                  error={Boolean(errors.name)}
                  errorMessage={errors.name}
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>
              <div className="flex-1" data-field="facility-phone">
                <TextField
                  fullWidth
                  label={FACILITY_LABELS.FIELD_PHONE}
                  type="tel"
                  maxLength={9}
                  value={entry.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  error={Boolean(errors.phone)}
                  errorMessage={errors.phone}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex-1" />
            </div>
          </div>

          <div className="border-b-2 border-[#DCDDE0]" />

          {/* ── Address ───────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-2">
            <p className="text-[18px] text-on-surface-variant">
              {FACILITY_LABELS.SECTION_ADDRESS}
            </p>

            <div className="flex flex-col gap-8">
              {/* Row: Address1 | Address2 | City */}
              <div className="flex gap-4">
                <div className="flex-1" data-field="facility-address1">
                  <TextField
                    fullWidth
                    label={FACILITY_LABELS.FIELD_ADDRESS1}
                    autoTrimMaxLength={200}
                    value={entry.address1}
                    onChange={(e) =>
                      updateField('address1', e.target.value.trimStart())
                    }
                    error={Boolean(errors.address1)}
                    errorMessage={errors.address1}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1">
                  <TextField
                    fullWidth
                    label={
                      <>
                        {FACILITY_LABELS.FIELD_ADDRESS2}
                        <span className="text-bodySmall font-normal">
                          {' '}
                          {FACILITY_LABELS.FIELD_OPTIONAL}
                        </span>
                      </>
                    }
                    dataTestId="facility-address2"
                    autoTrimMaxLength={200}
                    value={entry.address2}
                    onChange={(e) =>
                      updateField('address2', e.target.value.trimStart())
                    }
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1" data-field="facility-city">
                  <TextField
                    fullWidth
                    label={FACILITY_LABELS.FIELD_CITY}
                    autoTrimMaxLength={200}
                    value={entry.city}
                    onChange={(e) =>
                      updateField('city', e.target.value.trimStart())
                    }
                    error={Boolean(errors.city)}
                    errorMessage={errors.city}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Row: State | Zip | Country */}
              <div className="flex gap-4">
                <div className="flex-1" data-field="facility-state">
                  <TextField
                    fullWidth
                    label={FACILITY_LABELS.FIELD_STATE}
                    autoTrimMaxLength={200}
                    value={entry.state}
                    onChange={(e) =>
                      updateField('state', e.target.value.trimStart())
                    }
                    error={Boolean(errors.state)}
                    errorMessage={errors.state}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1" data-field="facility-zip">
                  <TextField
                    fullWidth
                    label={
                      <>
                        {FACILITY_LABELS.FIELD_ZIP}
                        <span className="text-bodySmall font-normal">
                          {' '}
                          {FACILITY_LABELS.FIELD_OPTIONAL}
                        </span>
                      </>
                    }
                    dataTestId="facility-zip"
                    maxLength={6}
                    value={entry.zip}
                    onChange={(e) =>
                      updateField('zip', e.target.value.trimStart())
                    }
                    error={Boolean(errors.zip)}
                    errorMessage={errors.zip}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1">
                  <TextField
                    fullWidth
                    label={FACILITY_LABELS.FIELD_COUNTRY}
                    value={entry.country}
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-b-2 border-[#DCDDE0]" />

          {/* ── Tax ───────────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-2">
            <p className="text-[18px] text-on-surface-variant">
              {FACILITY_LABELS.SECTION_TAX}
            </p>

            <div className="flex gap-4">
              <div className="flex-1" data-field="facility-nip">
                <TextField
                  fullWidth
                  label={FACILITY_LABELS.FIELD_NIP}
                  autoTrimMaxLength={13}
                  value={entry.nip}
                  onChange={(e) => handleNipChange(e.target.value)}
                  error={Boolean(errors.nip)}
                  errorMessage={errors.nip}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex-[2]" />
            </div>
          </div>

          <div className="border-b-2 border-[#DCDDE0]" />

          {/* ── Tabs: Aparatura / Pracownicy ──────────────────────────────── */}
          <div className="flex flex-col gap-6">
            {/* Tab bar row */}
            <div className="flex items-center justify-between">
              <div className="flex bg-[#F2F4F5] rounded-lg p-1 w-[432px] gap-1">
                {(
                  [
                    {
                      id: 'aparatura',
                      label: `${FACILITY_LABELS.SECTION_EQUIPMENT} (${canCreateEquipment && !isEquipmentAccessDenied ? (serverFacilityId ? equipmentTotalCount : submittedCount) : 0})`,
                    },
                    {
                      id: 'pracownicy',
                      label: `Pracownicy (${canCreateFacilityUser && !isEmployeeAccessDenied ? (serverFacilityId ? facilityUserTotalCount : submittedEmployeeCount) : 0})`,
                    },
                  ] as { id: TabId; label: string }[]
                ).map((tab) => {
                  const isDisabledByPermission =
                    (tab.id === 'pracownicy' &&
                      (!canCreateFacilityUser || isEmployeeAccessDenied)) ||
                    (tab.id === 'aparatura' &&
                      (!canCreateEquipment || isEquipmentAccessDenied));
                  const button = (
                    <button
                      type="button"
                      data-testid={`facility-tab-${tab.id}`}
                      onClick={() => handleTabChange(tab.id)}
                      disabled={
                        isSubmitting ||
                        isEmployeeSubmitting ||
                        isDisabledByPermission ||
                        privilegesLoading
                      }
                      className={[
                        'w-full py-1.5 rounded-md text-titleMedium font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] cursor-pointer',
                        activeTab === tab.id
                          ? 'bg-white text-primary-color-40 shadow-sm'
                          : 'text-outline hover:text-on-surface-variant',
                        isDisabledByPermission || privilegesLoading
                          ? 'opacity-38 cursor-not-allowed'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {tab.label}
                    </button>
                  );
                  return (
                    <div key={tab.id} className="flex-1">
                      {isDisabledByPermission ? (
                        <Tooltip
                          content={
                            tab.id === 'aparatura'
                              ? "You don't have permissions to create an equipment"
                              : "You don't have permissions to create an employee"
                          }
                          forceShow
                        >
                          {button}
                        </Tooltip>
                      ) : (
                        button
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Add equipment button — inline with tab bar */}
              {activeTab === 'aparatura' &&
                canCreateEquipment &&
                !isEquipmentAccessDenied &&
                (serverFacilityId ? equipmentTotalCount : submittedCount) > 0 &&
                !isAddingEquipment && (
                  <button
                    type="button"
                    onClick={() => {
                      clearSerialNumberConflict();
                      equipment.addEntry();
                      setIsAddingEquipment(true);
                    }}
                    data-testid="Dodaj aparaturę-button"
                    className="flex items-center gap-2 border border-primary-color-40 text-primary-color-40 rounded-[8px] px-4 py-[7px] text-[14px] font-medium leading-5 cursor-pointer hover:bg-primary-color-40/[0.08] disabled:opacity-38 disabled:cursor-not-allowed"
                    disabled={isSubmitting || equipment.isSubmitting}
                  >
                    <Plus size={20} />
                    {FACILITY_LABELS.ADD_EQUIPMENT_BUTTON_PRIMARY}
                  </button>
                )}
              {/* Add employee button — inline with tab bar */}
              {activeTab === 'pracownicy' &&
                canCreateFacilityUser &&
                !isEmployeeAccessDenied &&
                (serverFacilityId
                  ? facilityUserTotalCount
                  : submittedEmployeeCount) > 0 &&
                !isAddingEmployee && (
                  <button
                    type="button"
                    data-testid="Dodaj pracownika"
                    onClick={() => setIsAddingEmployee(true)}
                    className="flex items-center gap-2 border border-primary-color-40 text-primary-color-40 rounded-[8px] px-4 py-[7px] text-[14px] font-medium leading-5 cursor-pointer hover:bg-primary-color-40/[0.08] disabled:opacity-38 disabled:cursor-not-allowed"
                    disabled={isSubmitting || isEmployeeSubmitting}
                  >
                    <Plus size={20} />
                    {FACILITY_LABELS.ADD_EMPLOYEE_BUTTON}
                  </button>
                )}
            </div>

            {/* Tab content */}
            <div
              hidden={activeTab !== 'aparatura'}
              className={
                activeTab !== 'aparatura' ? 'hidden' : 'flex flex-col gap-6'
              }
            >
              {/* Tab-level loader — shown while permissions or session list fetch is in progress */}
              {(privilegesLoading || isEquipmentLoading) && (
                <div className="flex items-center justify-center py-6">
                  <Loader />
                </div>
              )}

              <div className="flex flex-col gap-4">
                {/* Pending forms — hidden while loading or no permission */}
                {!privilegesLoading &&
                  !isEquipmentLoading &&
                  !isEquipmentAccessDenied &&
                  isAddingEquipment &&
                  canCreateEquipment &&
                  pendingEntries.slice(-1).map((entry) => (
                    <AddEquipmentForm
                      key={entry.id}
                      entry={entry}
                      errors={
                        equipment.errorsList[
                          equipment.equipmentList.indexOf(entry)
                        ] ?? {
                          name: '',
                          description: '',
                          category: '',
                          serialNumber: '',
                          dateOfProduction: '',
                          modelName: '',
                          imageFile: '',
                          docFiles: '',
                        }
                      }
                      isSubmitting={
                        isSubmitting ||
                        isEquipmentSubmitting ||
                        equipment.isSubmitting
                      }
                      uploadProgress={
                        equipment.uploadProgressList[
                          equipment.equipmentList.indexOf(entry)
                        ]
                      }
                      failedUploadFiles={
                        equipment.failedUploadFilesList[
                          equipment.equipmentList.indexOf(entry)
                        ]
                      }
                      onFieldChange={(field, value) =>
                        equipment.updateField(entry.id, field, value)
                      }
                      onCategoryChange={(value) =>
                        equipment.updateCategory(entry.id, value)
                      }
                      onDateOfProductionChange={(date) =>
                        equipment.updateDateOfProduction(entry.id, date)
                      }
                      onImageChange={(files) =>
                        equipment.handleImageChange(entry.id, files)
                      }
                      onImageError={(msg) =>
                        equipment.handleImageError(entry.id, msg)
                      }
                      onDocsChange={(files) =>
                        equipment.handleDocsChange(entry.id, files)
                      }
                      onDocsError={(msg) =>
                        equipment.handleDocsError(entry.id, msg)
                      }
                      onRetry={(filename) =>
                        void equipment.retryUpload(entry.id, filename)
                      }
                      onSubmit={() => void handleEquipmentSubmit(entry.id)}
                      canClose={submittedCount > 0 || equipmentTotalCount > 0}
                      onCancel={() => {
                        if (submittedCount > 0 || equipmentTotalCount > 0) {
                          equipment.removeEntry(entry.id);
                          setIsAddingEquipment(false);
                        } else {
                          equipment.updateField(entry.id, 'name', '');
                          equipment.updateField(entry.id, 'description', '');
                          equipment.handleImageDelete(entry.id);
                          equipment.clearEntryErrors(entry.id);
                        }
                      }}
                    />
                  ))}

                {/* EquipmentList — always mounted when facilityId is set so
                        the fetch runs even on session restore; hidden via CSS
                        while loading or when no items exist (avoids empty
                        search bar + empty state showing alongside the form). */}
                {serverFacilityId && clientId && !isEquipmentAccessDenied && (
                  <div
                    className={
                      privilegesLoading ||
                      isEquipmentLoading ||
                      isAddingEquipment ||
                      !canCreateEquipment ||
                      (submittedCount === 0 && equipmentTotalCount === 0)
                        ? 'hidden'
                        : ''
                    }
                  >
                    <EquipmentList
                      clientId={clientId}
                      facilityId={serverFacilityId}
                      refreshKey={submittedCount + equipmentEditCount}
                      resetKey={equipmentListResetKey}
                      canEdit={canEditEquipment}
                      onEquipmentEdited={() =>
                        setEquipmentEditCount((prev) => prev + 1)
                      }
                      onTotalCountChange={handleEquipmentTotalCountChange}
                      onCancelEdit={clearSerialNumberConflict}
                      onAccessDenied={handleEquipmentAccessDenied}
                      onEditingChange={setIsEquipmentEditDirty}
                      disabled={isSubmitting}
                      conflictsByEquipmentId={
                        serialNumberConflict &&
                        serialToEquipmentId[serialNumberConflict]
                          ? {
                              [serialToEquipmentId[serialNumberConflict]]:
                                TL.SERIAL_NUMBER_DUPLICATE.polish,
                            }
                          : undefined
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            <div
              hidden={activeTab !== 'pracownicy'}
              className={
                activeTab !== 'pracownicy' ? 'hidden' : 'flex flex-col gap-4'
              }
            >
              {/* Tab-level loader — shown while permissions are being checked */}
              {privilegesLoading && (
                <div className="flex items-center justify-center py-6">
                  <Loader />
                </div>
              )}

              {/* List — visible once isAddingEmployee = false */}
              {!privilegesLoading &&
                canCreateFacilityUser &&
                !isEmployeeAccessDenied &&
                serverFacilityId &&
                !isAddingEmployee && (
                  <EmployeeList
                    facilityId={serverFacilityId}
                    refreshKey={submittedEmployeeCount}
                    resetKey={employeeListResetKey}
                    canEdit={canEditFacilityUser && !isEmployeeAccessDenied}
                    canToggle={canToggleEmployeeStatus}
                    isToggleDisabled={isToggleDisabled}
                    toggleDisabledReason={toggleDisabledReason}
                    onTotalCountChange={handleFacilityUserTotalCountChange}
                    onAccessDenied={handleEmployeeAccessDenied}
                    onEditingChange={setIsEmployeeEditDirty}
                    conflictsByEmployeeId={
                      conflictError
                        ? Object.fromEntries(
                            conflictError.map((c) => [c.employeeId, c.email])
                          )
                        : undefined
                    }
                    disabled={isSubmitting}
                  />
                )}

              {/* Form — visible while isAddingEmployee = true.
                    Starts true; flips to false after first successful submit
                    or after page-restore confirms employees exist in DB. */}
              {!privilegesLoading &&
                !isEmployeeAccessDenied &&
                isAddingEmployee && (
                  <AddEmployeeForm
                    entry={activeEmployeeEntry}
                    errors={
                      employeeErrorsMap[activeEmployeeEntry.id] ?? {
                        name: '',
                        email: '',
                        phone: '',
                        gender: '',
                        academicTitle: '',
                      }
                    }
                    isSubmitting={isSubmitting || isEmployeeSubmitting}
                    cancelAlwaysEnabled={
                      submittedEmployeeCount > 0 || facilityUserTotalCount > 0
                    }
                    onFieldChange={(field, value) =>
                      updateEmployeeField(activeEmployeeEntry.id, field, value)
                    }
                    onGenderChange={(value) =>
                      updateEmployeeGender(activeEmployeeEntry.id, value)
                    }
                    onSubmit={() => {
                      if (clientId) {
                        void submitEmployee(clientId);
                      }
                    }}
                    onCancel={() => {
                      // Always clear the fields so isEmployeeFormDirty resets and the
                      // main submit button re-enables regardless of whether the form
                      // collapses or stays visible.
                      updateEmployeeField(activeEmployeeEntry.id, 'name', '');
                      updateEmployeeField(activeEmployeeEntry.id, 'email', '');
                      updateEmployeeField(activeEmployeeEntry.id, 'phone', '');
                      updateEmployeeField(
                        activeEmployeeEntry.id,
                        'academicTitle',
                        ''
                      );
                      updateEmployeeGender(activeEmployeeEntry.id, undefined);
                      if (
                        submittedEmployeeCount > 0 ||
                        facilityUserTotalCount > 0
                      ) {
                        // "+" mode — collapse to list after clearing
                        setIsAddingEmployee(false);
                      }
                    }}
                  />
                )}
            </div>
          </div>
        </div>
      </CreateLayout>

      <Snackbar
        open={isSnackbarOpen}
        message={snackbarError}
        variant="error"
        onClose={handleCloseSnackbar}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isEquipmentSuccessOpen}
        message={FACILITY_LABELS.ADD_EQUIPMENT_SUCCESS}
        variant="success"
        onClose={() => setIsEquipmentSuccessOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isEquipmentErrorOpen}
        message={equipmentErrorMessage}
        variant="error"
        onClose={() => setIsEquipmentErrorOpen(false)}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isEmployeeSuccessOpen}
        message={FACILITY_LABELS.ADD_EMPLOYEE_SUCCESS}
        variant="success"
        onClose={() => setIsEmployeeSuccessOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
    </>
  );
}
